import React, { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const StoryMedia = ({
    story,
    isActive,
    isPaused,
    onVideoDuration,
    onMediaLoad,
    onVideoEnded,
    setVideoRef,
    isMuted, // New prop
    preload = false // New prop
}) => {
    const videoRef = useRef(null);
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(true);

    // Determine if it's a video
    const isVideo = story.type === 'video' ||
        story.video ||
        (story.image && (story.image.includes('.mp4') || story.image.includes('.webm') || story.image.includes('.mov')));

    const mediaSrc = story.video || story.image;

    useEffect(() => {
        if (setVideoRef) {
            setVideoRef(videoRef.current);
        }
    }, [setVideoRef]);

    // Handle Play/Pause
    useEffect(() => {
        if (preload) return; // Don't play if preloading

        const video = videoRef.current;
        if (video && isActive) {
            if (isPaused) {
                video.pause();
            } else {
                video.play().catch(err => console.log('Autoplay prevented:', err));
            }
        } else if (video) {
            // Pause if not active story
            video.pause();
            video.currentTime = 0;
        }
    }, [isActive, isPaused, preload]);

    if (!story) return null;

    if (preload) {
        // Render minimal hidden version for caching
        return (
            <div style={{ display: 'none' }}>
                {isVideo ? (
                    <video src={mediaSrc} preload="auto" muted draggable={false} />
                ) : (
                    <img src={mediaSrc} alt="preload" draggable={false} />
                )}
            </div>
        );
    }

    return (
        <div
            style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                backgroundColor: '#000'
            }}
        >
            {/* Loading Spinner */}
            {isLoading && (
                <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    color: 'white',
                    zIndex: 10
                }}>
                    Loading...
                </div>
            )}

            {/* Thumbnail Placeholder for Video */}
            {isVideo && isLoading && story.thumbnail_url && (
                <img
                    src={story.thumbnail_url}
                    alt="Thumbnail"
                    draggable={false}
                    onDragStart={(e) => e.preventDefault()}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        zIndex: 5,
                        userSelect: 'none',
                        WebkitUserSelect: 'none',
                        WebkitUserDrag: 'none',
                        pointerEvents: 'auto'
                    }}
                />
            )}

            {isVideo ? (
                <video
                    ref={videoRef}
                    src={mediaSrc}
                    draggable={false}
                    onDragStart={(e) => e.preventDefault()}
                    style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        opacity: isLoading ? 0 : 1, // Use opacity properly on video
                        transition: 'opacity 0.3s ease',
                        position: 'relative', // Ensure video is positioned
                        zIndex: 6, // Ensure video is above thumbnail when loaded
                        userSelect: 'none',
                        WebkitUserSelect: 'none',
                        WebkitUserDrag: 'none',
                        pointerEvents: 'auto'
                    }}
                    playsInline
                    muted={isMuted} // Controlled by parent
                    // Start muted for autoplay policies, user can unmute if needed (though stories usually allow sound)
                    // Note: Instagram stories play sound by default if ringer is on, but web browsers block unmuted autoplay.
                    // We might need to handle this. For now let's try unmuted but catch errors.
                    onLoadedMetadata={(e) => {
                        if (onVideoDuration) onVideoDuration(e.target.duration * 1000);
                    }}
                    onCanPlay={(e) => {
                        // Only consider loaded when we can actually play
                        if (isLoading) {
                            setIsLoading(false);
                            if (onMediaLoad) onMediaLoad();

                            e.target.muted = false; // Try to unmute
                            if (isActive && !isPaused) {
                                e.target.play().catch(() => {
                                    // If unmuted playback fails, we stay muted or show a warning. 
                                    // But typically browsers allow unmuted playback if it's user-initiated or already unmuted.
                                    console.log("Play failed, retrying muted");
                                    e.target.muted = true;
                                    e.target.play().catch(err => console.log("Muted play also failed:", err));
                                });
                            }
                        }
                    }}
                    onEnded={onVideoEnded}
                    onError={(e) => {
                        console.error("Video load error", e);
                        setIsLoading(false);
                        if (onMediaLoad) onMediaLoad(); // Proceed even on error to avoid stuck timer
                    }}
                />
            ) : (
                <img
                    src={mediaSrc}
                    alt={story.caption || 'Story'}
                    draggable={false}
                    onDragStart={(e) => e.preventDefault()}
                    style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        opacity: isLoading ? 0 : 1, // Use opacity for smoother transition
                        transition: 'opacity 0.3s ease',
                        userSelect: 'none',
                        WebkitUserSelect: 'none',
                        WebkitUserDrag: 'none',
                        pointerEvents: 'auto'
                    }}
                    onLoad={() => {
                        setIsLoading(false);
                        if (onMediaLoad) onMediaLoad(); // Notify parent
                    }}
                    onError={(e) => {
                        setIsLoading(false);
                        if (onMediaLoad) onMediaLoad(); // Proceed even on error
                        // e.target.src = '/placeholder.png'; // Optional fallback
                    }}
                />
            )}

            {/* Caption Overlay */}
            {story.caption && (
                <div style={{
                    position: 'absolute',
                    bottom: '10%',
                    left: 0,
                    right: 0,
                    textAlign: 'center',
                    padding: '10px 20px',
                    color: 'white',
                    textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontSize: '16px',
                    background: 'linear-gradient(to top, rgba(0,0,0,0.5), transparent)',
                    pointerEvents: 'none'
                }}>
                    {story.caption}
                </div>
            )}
        </div>
    );
};

export default StoryMedia;
